// web/src/features/availability/state/use-debounced-mark-persist.js
//
// Debounced auto-save for availability mark drag/resize edits.
//
// Why: rapid drag-and-drop on RBC events fires onEventDrop / onEventResize
// many times in quick succession (the user dragging a mark over several
// candidate slots before settling). Writing each intermediate position to
// Firestore would (a) waste writes, (b) trigger N engine evaluations, and
// (c) potentially cascade vote overrides for transient states.
//
// Pattern: optimistic UI + debounced commit + page-leave flush. Same shape
// as Google Docs / Figma / Notion auto-save. Per-mark patch accumulator,
// 1.5s settle, force-flush on pagehide / visibilitychange-to-hidden.
// Trust Firestore's offline persistence (built-in IndexedDB retry queue)
// for the rare case where the page actually unloads mid-flush.

import { useCallback, useEffect, useRef } from "react";

import { updateAvailabilityMark } from "../../../lib/data/availability.js";

const DEFAULT_DELAY_MS = 1500;

export function useDebouncedMarkPersist(uid, delayMs = DEFAULT_DELAY_MS) {
  // markId -> merged patch object accumulated since last flush
  const pendingPatches = useRef(new Map());
  // markId -> setTimeout handle for the trailing-edge write
  const timers = useRef(new Map());
  // Mirror uid in a ref so the trailing-edge timer reads the latest value
  // when it fires (closure-captured uid would go stale across re-renders).
  const uidRef = useRef(uid);
  useEffect(() => {
    uidRef.current = uid;
  }, [uid]);

  const flushOne = useCallback(async (markId) => {
    const patch = pendingPatches.current.get(markId);
    if (!patch) return;
    pendingPatches.current.delete(markId);
    const handle = timers.current.get(markId);
    if (handle) clearTimeout(handle);
    timers.current.delete(markId);
    const currentUid = uidRef.current;
    if (!currentUid) return;
    try {
      await updateAvailabilityMark(currentUid, markId, patch);
    } catch (err) {
      // Firestore SDK retries via offline persistence; surfacing a toast
      // here would be the right escalation path if/when this ships behind
      // a UI surface that can host one.
      console.error("[availability] debounced mark write failed", err);
    }
  }, []);

  const queueUpdate = useCallback((markId, patch) => {
    if (!uidRef.current) return;
    if (!markId || !patch) return;
    const prev = pendingPatches.current.get(markId) || {};
    pendingPatches.current.set(markId, { ...prev, ...patch });
    const existing = timers.current.get(markId);
    if (existing) clearTimeout(existing);
    const handle = setTimeout(() => {
      flushOne(markId);
    }, delayMs);
    timers.current.set(markId, handle);
  }, [delayMs, flushOne]);

  const flushAll = useCallback(() => {
    const ids = [...pendingPatches.current.keys()];
    ids.forEach((id) => flushOne(id));
  }, [flushOne]);

  // Page-leave guard: when the tab is closing or hidden, fire all pending
  // writes immediately. Firestore offline persistence handles the case
  // where the network call doesn't complete before unload.
  useEffect(() => {
    const onPageHide = () => flushAll();
    const onVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        flushAll();
      }
    };
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", onPageHide);
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("pagehide", onPageHide);
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
    };
  }, [flushAll]);

  // Cleanup on unmount: flush whatever is still queued so navigation
  // within the SPA (e.g. router push to /dashboard) doesn't drop edits.
  useEffect(() => () => flushAll(), [flushAll]);

  return { queueUpdate, flushAll };
}
